import { Link, useLocation } from "react-router-dom";
import { Icon, IconName } from "../Icon";
import { useI18n } from "../i18n";
import { AdminIndexPage } from "./AdminIndex";
import { AdminStatsPage } from "./AdminStats";
import { AdminUsersPage } from "./AdminUsers";
import { AdminActivityPage } from "./AdminActivity";
import { AdminGamesPage } from "./AdminGames";
import { AdminVenuesPage } from "./AdminVenues";
import { AdminReportsPage } from "./AdminReports";
import { AdminRatingsPage } from "./AdminRatings";
import { AdminFbPage } from "./AdminFb";
import { AdminHeatmapPage } from "./AdminHeatmap";
import "./Admin.css";

type SubPage =
  | "stats"
  | "users"
  | "activity"
  | "games"
  | "venues"
  | "reports"
  | "ratings"
  | "fb"
  | "heatmap";

const SUB_PAGE_META: Record<SubPage, { labelKey: string; icon: IconName }> = {
  stats:    { labelKey: "admin.stats",    icon: "chart-line-data-01" },
  users:    { labelKey: "admin.users",    icon: "user-account" },
  activity: { labelKey: "admin.activity", icon: "clock-01" },
  games:    { labelKey: "admin.games",    icon: "tennis-ball" },
  venues:   { labelKey: "admin.venues",   icon: "building-01" },
  reports:  { labelKey: "admin.reports",  icon: "flag-01" },
  ratings:  { labelKey: "admin.ratings",  icon: "award-01" },
  fb:       { labelKey: "admin.fb",       icon: "globe" },
  heatmap:  { labelKey: "admin.heatmap",  icon: "chart-bar" },
};

/**
 * /admin landing page. With no `subPage` prop it shows the index of cards
 * linking to the admin sub-pages. With a `subPage` prop it shows the
 * matching sub-page (and a "back to admin" link in the header).
 */
export function AdminPage({ subPage }: { subPage?: SubPage }) {
  const { t } = useI18n();
  const location = useLocation();

  // /admin and /admin/stats etc. — when subPage is set, render the matching page.
  if (subPage) {
    const meta = SUB_PAGE_META[subPage];
    const label = t(meta.labelKey);
    return (
      <div className="adminPage">
        <header className="page-header">
          <Link
            to="/admin"
            className="btn btn-ghost btn-icon"
            aria-label="Back to admin"
            data-analytics-label="admin-back"
            state={{ from: location.pathname }}
          >
            <Icon name="arrow-left-01" size={16} />
          </Link>
          <div className="page-header-icon">
            <Icon name={meta.icon} size={20} />
          </div>
          <div>
            <h1 className="page-header-title">{label}</h1>
            <p className="page-header-sub">{t('admin.subtitleSection', { section: label })}</p>
          </div>
        </header>
        {subPage === "stats"    && <AdminStatsPage />}
        {subPage === "users"    && <AdminUsersPage />}
        {subPage === "activity" && <AdminActivityPage />}
        {subPage === "games"    && <AdminGamesPage />}
        {subPage === "venues"   && <AdminVenuesPage />}
        {subPage === "reports"  && <AdminReportsPage />}
        {subPage === "ratings"  && <AdminRatingsPage />}
        {subPage === "fb"       && <AdminFbPage />}
        {subPage === "heatmap"  && <AdminHeatmapPage />}
      </div>
    );
  }

  return (
    <div className="adminPage">
      <header className="page-header">
        <div className="page-header-icon">
          <Icon name="crown" size={20} />
        </div>
        <div>
          <h1 className="page-header-title">{t('admin.title')}</h1>
          <p className="page-header-sub">{t('admin.subtitle')}</p>
        </div>
      </header>
      <AdminIndexPage />
    </div>
  );
}
